import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { getMaskedCursorPosition } from '../helpers';
import { ITextFieldMask, TTextFieldAllowedChars } from '../types';

function filterAllowedChars(value: string, allowedChars?: TTextFieldAllowedChars) {
  if (!allowedChars) {
    return value;
  }

  if (typeof allowedChars === 'string') {
    const allowedSet = new Set(Array.from(allowedChars));

    return Array.from(value)
      .filter((char) => allowedSet.has(char))
      .join('');
  }

  const regex = new RegExp(allowedChars.source, allowedChars.flags.replaceAll(/[gy]/g, ''));

  return Array.from(value)
    .filter((char) => regex.test(char))
    .join('');
}

export function useHandleMaskedChange<T extends FieldValues>(
  mask?: ITextFieldMask,
  storeMaskValue?: boolean,
  allowedChars?: TTextFieldAllowedChars
) {
  return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: ControllerRenderProps<T>) => {
    const input = e.target;
    const inputValue = filterAllowedChars(input.value, allowedChars);
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const valueBeforeSelectionStart = filterAllowedChars(input.value.slice(0, selectionStart), allowedChars);
    const valueBeforeSelectionEnd = filterAllowedChars(input.value.slice(0, selectionEnd), allowedChars);

    if (!mask) {
      field.onChange(inputValue);
      requestAnimationFrame(() => {
        input.setSelectionRange(valueBeforeSelectionStart.length, valueBeforeSelectionEnd.length);
      });
      return;
    }

    const rawCharactersBeforeSelectionStart = mask.parse(valueBeforeSelectionStart).length;
    const rawCharactersBeforeSelectionEnd = mask.parse(valueBeforeSelectionEnd).length;
    const rawValue = mask.parse(inputValue);
    const maskedValue = mask.format(rawValue);
    const nextSelectionStart = getMaskedCursorPosition(mask, maskedValue, rawCharactersBeforeSelectionStart);
    const nextSelectionEnd = getMaskedCursorPosition(mask, maskedValue, rawCharactersBeforeSelectionEnd);

    field.onChange(storeMaskValue ? maskedValue : rawValue);
    requestAnimationFrame(() => {
      input.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    });
  };
}
